/**
 * useForgotPin — self-service PIN reset.
 *
 * Calls the forgot-pin Edge Function. The server ALWAYS returns 200
 * with a generic body regardless of whether the email is on file
 * (no enumeration). The hook reflects that in its UI semantics:
 * `submitted` becomes true after a successful POST, and the LoginPage
 * shows a generic "If that email is registered, you'll receive a new
 * PIN" message — never a per-email confirmation.
 *
 * The hook surfaces 429 rate-limit responses distinctly so the UI can
 * back off the user with a clear "wait and try again" message; that
 * is not user-enumeration leak (anyone can hit the rate limit).
 */

import { useState }   from 'react'
import { supabase }   from '@/lib/supabase'
import { AppError }   from '@/lib/errors/AppError'

export interface ForgotPinResult {
  requestReset: (email: string) => Promise<void>
  /** True after a successful (200) submission — UI should show the generic confirmation. */
  submitted:    boolean
  isPending:    boolean
  error:        AppError | null
  reset:        () => void
}

export function useForgotPin(): ForgotPinResult {
  const [submitted, setSubmitted] = useState(false)
  const [isPending, setPending]   = useState(false)
  const [error,     setError]     = useState<AppError | null>(null)

  const requestReset = async (rawEmail: string): Promise<void> => {
    const email = rawEmail.trim().toLowerCase()
    setPending(true)
    setError(null)
    setSubmitted(false)

    let invokeError: { message: string; status?: number; context?: Response } | null = null
    try {
      const result = await supabase.functions.invoke('forgot-pin', {
        body: { email },
      })
      invokeError = result.error as typeof invokeError
    } catch (e) {
      invokeError = { message: e instanceof Error ? e.message : 'Network error' }
    }

    if (invokeError) {
      const status = invokeError.status ?? 500
      let serverCode   = 'FORGOT_PIN_FAILED'
      let serverDetail = invokeError.message
      if (invokeError.context) {
        try {
          const body = await invokeError.context.clone().json() as { error?: string; detail?: string }
          if (typeof body.error === 'string')  serverCode   = body.error
          if (typeof body.detail === 'string') serverDetail = body.detail
        } catch { /* keep defaults */ }
      }
      const appErr = new AppError({
        code:        serverCode,
        message:     serverDetail,
        userMessage: friendlyMessage(serverCode, serverDetail),
        status,
      })
      setPending(false)
      setError(appErr)
      throw appErr
    }

    setPending(false)
    setSubmitted(true)
  }

  const reset = (): void => {
    setSubmitted(false)
    setError(null)
  }

  return { requestReset, submitted, isPending, error, reset }
}

function friendlyMessage(code: string, detail: string): string {
  switch (code) {
    case 'RATE_LIMITED':
      return 'Too many reset requests. Please wait a few minutes and try again.'
    case 'INVALID_BODY':
      return 'That email address is not in a valid format.'
    default:
      return detail || 'Could not send a reset email. Please try again.'
  }
}
